<template>
    <div class="bg-gray-50 p-4">
        <div class="flex flex-wrap items-start font-semibold text-gray-600">
            <span class="whitespace-nowrap">
                Choose Participant #{{ props.sequence }}
            </span>
            <span
                v-if="
                    (enrollment.data?.type === 'new' &&
                        customerOptions.length > 0) ||
                    (enrollment.data?.type === 'existing' && showAddByBookingId)
                "
                class="px-2"
            >
                &mdash;
                <prime-button
                    link
                    class="!px-2 !py-0"
                    :label="$t('purchaseContract.selectExistingParticipant')"
                    @click="handleSelectCustomer(null)"
                />
            </span>
        </div>

        <div class="flex flex-col items-center gap-2 md:flex-row">
            <template v-if="enrollment.data?.type !== 'new'">
                <enrollment-add-by-booking-id
                    v-if="showAddByBookingId"
                    v-model:visible="showAddByBookingId"
                    :show-errors="showErrors"
                    :course-id="courseId"
                    @add-enrollment="handleUpdateByBookingId"
                    @back="handleSelectCustomer(null)"
                />

                <prime-dropdown
                    v-else
                    :model-value="selectedOption"
                    :options="customerOptions"
                    option-label="commaName"
                    option-disabled="disabled"
                    :placeholder="
                        $t('purchaseContract.selectFromExistingHousehold')
                    "
                    show-clear
                    class="md:w-[25rem]"
                    :class="dropdownClass"
                    :pt="{
                        item: ({ context }) => ({
                            class: [
                                'cursor-pointer font-normal overflow-hidden relative whitespace-nowrap',
                                'm-0 p-3 border-0 transition-shadow duration-200 rounded-none',
                                {
                                    'text-gray-400 dark:text-white/80':
                                        !context.focused &&
                                        !context.selected &&
                                        context.disabled,
                                    'text-gray-700 hover:text-gray-700 hover:bg-gray-200 dark:text-white/80 dark:hover:bg-gray-800':
                                        !context.focused &&
                                        !context.selected &&
                                        !context.disabled,
                                    'bg-gray-300 text-gray-700 dark:text-white/80 dark:bg-gray-800/90 hover:text-gray-700 hover:bg-gray-200 dark:text-white/80 dark:hover:bg-gray-800':
                                        context.focused &&
                                        !context.selected &&
                                        !context.disabled,
                                    'bg-primary-100 text-primary-700 dark:bg-primary-400 dark:text-white/80':
                                        context.focused && context.selected,
                                    'bg-primary-50 text-primary-700 dark:bg-primary-300 dark:text-white/80':
                                        !context.focused && context.selected
                                }
                            ]
                        })
                    }"
                    aria-label="Select Customer"
                    @update:model-value="handleSelectCustomer"
                />
            </template>

            <enrollment-new-customer
                v-if="enrollment.data?.type === 'new'"
                :enrollment="enrollment.data"
                :identifier="`${sequence}enrollmentNewCustomer`"
                :show-errors="showErrors"
                class="pt-1 md:pt-0"
                :is-guest="false"
                @update:enrollment="handleUpdateNewEnrollment"
            />

            <template
                v-if="
                    enrollment.data?.type !== 'existing' &&
                    enrollment.data?.type !== 'new'
                "
            >
                <div
                    class="mx-6 flex flex-col items-center whitespace-nowrap text-lg font-semibold lg:mx-10 lg:flex-row"
                >
                    {{ $t('purchaseContract.or') }}
                </div>

                <div class="flex flex-col items-stretch gap-4 lg:flex-row">
                    <prime-button
                        outlined
                        icon="pi pi-plus"
                        :label="$t('purchaseContract.addNewCustomer')"
                        @click="handleAddNew"
                    />

                    <prime-button
                        outlined
                        icon="pi pi-star-fill"
                        :pt="{
                            icon: '!text-star'
                        }"
                        :pt-options="{ mergeProps: true }"
                        :label="`Add Friends and Family by ${bookingIdName}`"
                        @click="handleAddByBookingId"
                    />
                </div>
            </template>
        </div>

        <div
            v-if="enrollmentWarningMessage !== '' && showErrors"
            class="text-red-600 font-medium"
        >
            {{ enrollmentWarningMessage }}
        </div>

        <div
            v-if="!showErrors && alreadyOnWaitlistErrorMessage !== ''"
            class="text-red-600 font-medium"
        >
            {{ alreadyOnWaitlistErrorMessage }}
        </div>

        <app-message
            v-if="
                enrollmentWarningMessage === '' &&
                waitlistConstraintInfo !== null
            "
            :theme="waitlistConstraintInfo.required ? 'danger' : 'warning'"
            class="mt-4 flex"
        >
            {{ waitlistConstraintInfo.message }}
        </app-message>

        <div
            v-if="collectAdditionalComments"
            class="pt-3"
        >
            <label
                :for="`additionalCommentsInput${sequence}`"
                class="text-gray-600"
                >Additional Comments</label
            >
            <div>
                <input-text
                    :id="`additionalCommentsInput${sequence}`"
                    :model-value="additionalComments"
                    class="w-full md:w-1/2"
                    maxlength="500"
                    @update:model-value="handleUpdateAdditionalComments"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import PrimeButton from 'primevue/button'
import PrimeDropdown from 'primevue/dropdown'
import type {
    EnrollmentInputEnrollmentData,
    EnrollmentInputExistingEnrollment,
    EnrollmentInputNewEnrollment
} from '~/composables/enrollment-input'
import EnrollmentNewCustomer from '~/components/PurchaseContract/CustomerPicker/EnrollmentNewCustomer.vue'
import { computed, onMounted, toRef, ref, watch } from 'vue'
import { now, parseTimeless } from '~/composables/support/localization'
import { useActiveFacility } from '~/composables/use-active-facility'
import { differenceInYears, isAfter } from 'date-fns'
import { useI18n } from 'vue-i18n'
import { randomString } from '~/composables/support/util'
import EnrollmentAddByBookingId from '~/components/PurchaseContract/CustomerPicker/EnrollmentAddByBookingId.vue'
import { useBookingIdName } from '~/composables/use-org'
import type {
    WaitlistEnrollmentInput,
    WaitlistEnrollmentInputEnrollment,
    WaitlistEnrollmentInputExistingParticipant
} from '~/composables/wait-list-enrollment-input'
import InputText from 'primevue/inputtext'
import type { WaitlistedCustomerConstraints } from '~/components/Waitlist/waitlisted-customer-constraint'
import AppMessage from '~/components/AppMessage.vue'

const props = defineProps<{
    sequence: number
    enrollment: WaitlistEnrollmentInput
    participantOptions: (WaitlistEnrollmentInputExistingParticipant & {
        disabled: boolean
    })[]
    collectAdditionalComments: boolean
    waitlistConstraints: WaitlistedCustomerConstraints
    showErrors: boolean
    courseId: string
}>()

const emit = defineEmits<{
    (e: 'update:enrollment', payload: WaitlistEnrollmentInput): void
}>()

const bookingIdName = useBookingIdName()
const { t } = useI18n()
const customerOptions = computed(() => {
    return props.participantOptions.map(customer => {
        if (
            props.enrollment.data !== null &&
            props.enrollment.data.type === 'existing' &&
            props.enrollment.data.id === customer.id
        ) {
            return {
                ...customer,
                disabled: false
            }
        } else {
            return customer
        }
    })
})

const selectedOption = computed(() => {
    return customerOptions.value.find(
        customer =>
            props.enrollment.data !== null &&
            props.enrollment.data.type === 'existing' &&
            props.enrollment.data.id === customer.id
    )
})

function handleSelectCustomer(
    customer:
        | (WaitlistEnrollmentInputExistingParticipant & { disabled: boolean })
        | null
) {
    if (customer === null) {
        showAddByBookingId.value = false
        emitUpdatedEnrollmentData(null)
    } else if (customer.disabled) {
        return
    } else {
        emitUpdatedEnrollmentData({
            type: 'existing',
            id: customer.id,
            commaName: customer.commaName,
            firstName: customer.firstName,
            middleName: customer.middleName,
            lastName: customer.lastName,
            dateOfBirth: customer.dateOfBirth,
            alreadyOnWaitlist: customer.alreadyOnWaitlist
        })
    }
}

function handleAddNew() {
    showAddByBookingId.value = false
    emitUpdatedEnrollmentData({
        type: 'new',
        key: randomString(),
        firstName: '',
        lastName: '',
        middleName: '',
        dateOfBirth: '',
        householdMember: true
    })
}

function handleUpdateNewEnrollment(data: EnrollmentInputNewEnrollment) {
    emitUpdatedEnrollmentData(data)
}

const showAddByBookingId = ref(false)
function handleAddByBookingId() {
    showAddByBookingId.value = true

    // This is our placeholder while we wait to add the customer
    emitUpdatedEnrollmentData({
        type: 'existing',
        id: '',
        commaName: '',
        firstName: '',
        middleName: '',
        lastName: '',
        dateOfBirth: '',
        alreadyOnWaitlist: false
    })
}

function handleUpdateByBookingId(data: EnrollmentInputExistingEnrollment) {
    const newlyAddedCustomer = customerOptions.value.find(
        option => option.id === data.id
    )
    // Ensure that no race condition exists:
    // By the time the booking ID component emits,
    // We should already have the emitted customer as a saved participant option
    if (newlyAddedCustomer === undefined) {
        throw new Error('handleUpdateByBookingIdError')
    }
    emitUpdatedEnrollmentData(newlyAddedCustomer)
}

const activeFacility = useActiveFacility()
function getAgeRestrictionWarningMessage(
    data: WaitlistEnrollmentInputEnrollment
) {
    // If enrollment hasn't been selected yet return nothing
    if (data === null) {
        return ''
    }

    const customerDob = parseTimeless(data.dateOfBirth)
    const customerAge = differenceInYears(
        now(activeFacility.value.timezone),
        customerDob
    )

    // For new enrollments
    if (isNaN(customerDob.getTime())) {
        return t('purchaseContract.fillOutDateOfBirth')
    } else if (isAfter(customerDob, now(activeFacility.value.timezone))) {
        return t('purchaseContract.futureDateOfBirthWarning')
    } else if (customerAge > 120) {
        return t('purchaseContract.fillOutDateOfBirth')
    }

    return ''
}

function getAlreadyOnWaitlistErrorMessage(
    data: WaitlistEnrollmentInputEnrollment
) {
    // If enrollment hasn't been selected yet, or it is a new enrollment
    // return nothing
    if (data === null || data.type === 'new') {
        return ''
    }

    if (data.alreadyOnWaitlist) {
        return 'The selected customer is already on the waitlist for this course.'
    }

    return ''
}

const alreadyOnWaitlistErrorMessage = computed(() => {
    return getAlreadyOnWaitlistErrorMessage(props.enrollment.data)
})

const enrollmentWarningMessage = computed(() => {
    // If we are adding by booking ID return
    if (showAddByBookingId.value) {
        return ''
    }

    const message = getAlreadyOnWaitlistErrorMessage(props.enrollment.data)

    if (message !== '') {
        return message
    }

    return getAgeRestrictionWarningMessage(props.enrollment.data)
})

function getWaitlistConstraintWarningMessage(
    data: EnrollmentInputEnrollmentData
) {
    if (data === null) {
        return null
    }

    let key
    // Actual customer record
    if (data.type === 'existing') {
        key = data.id
    } else {
        key = data.key
    }

    const constraint =
        props.waitlistConstraints.waitlistedCustomerConstraintSegmentLookup.value.get(
            key
        )

    if (constraint === undefined || constraint.valid === true) {
        return null
    }

    return constraint
}

const waitlistConstraintInfo = computed(() => {
    return getWaitlistConstraintWarningMessage(props.enrollment.data)
})

onMounted(() => {
    // Make sure invalid is set properly
    emitUpdatedEnrollmentData(props.enrollment.data)
})

// Watch for any changes if we are out of sync with invalid, emit a new invalid
watch(
    [toRef(props, 'enrollment'), waitlistConstraintInfo],
    ([newEnrollment]) => {
        const { invalid } = isEnrollmentInvalid(newEnrollment.data)
        if (invalid !== newEnrollment.invalid) {
            emitUpdatedEnrollmentData(newEnrollment.data)
        }
    }
)

function isEnrollmentInvalid(data: WaitlistEnrollmentInputEnrollment) {
    let invalid = false
    let invalidFromConstraint = false
    if (data === null) {
        invalid = true
    } else if (getAgeRestrictionWarningMessage(data) !== '') {
        invalid = true
    } else if (data.type === 'new' && data.firstName.length < 2) {
        invalid = true
    } else if (data.type === 'new' && data.lastName.length < 2) {
        invalid = true
    } else if (data.type === 'existing' && data.alreadyOnWaitlist) {
        invalid = true
    } else {
        const constraint = getWaitlistConstraintWarningMessage(data)
        if (constraint !== null && constraint.required) {
            invalid = true
            invalidFromConstraint = true
        }
    }

    return { invalid, invalidFromConstraint }
}

function emitUpdatedEnrollmentData(value: WaitlistEnrollmentInputEnrollment) {
    const { invalid, invalidFromConstraint } = isEnrollmentInvalid(value)
    emit('update:enrollment', {
        data:
            value === null
                ? null
                : {
                      ...value
                  },
        additionalComments: additionalComments.value,
        invalid,
        invalidFromConstraint
    })
}

const dropdownClass = computed(() => {
    // If we have something selected, no highlighting needed
    if (selectedOption.value) {
        return ''
    }
    // Else if we have nothing selected and show error flag is on, highlight red
    else if (props.showErrors) {
        return '!border-red-500 ring ring-red-400/50'
    }
    // Else highlight yellow
    else {
        return '!border-yellow-500 ring ring-yellow-400/50'
    }
})

const additionalComments = ref('')
function handleUpdateAdditionalComments(data: string) {
    additionalComments.value = data
    emitUpdatedEnrollmentData(props.enrollment.data)
}
</script>
